import uniqid from 'uniqid';
import ReactiveComponent, { filter, watch } from '@utils/reactive-component';
import SelectBox from '../select-box/select-box';
import template from './templates/cart-autoship';
import { defaults } from './cart-mapping';
import isFn from '@utils/is-function';

const DEFAULT_OPTIONS = {
    frequency: 0,
    replaceOriginEl: true,
    subscriptionValue: 7
};

export default class CartAutoship extends ReactiveComponent {
    #watch = {};

    constructor($originEl, context, options = DEFAULT_OPTIONS) {
        super();

        context = { ...defaults.context, ...context };
        options = { ...DEFAULT_OPTIONS, ...options };

        const parsed = $.parseHTML(template);
        this.$el = $(parsed);
        this.$originEl = $originEl;

        this.$options = this.$el.find('[data-region="options"]');
        this.$frequency = this.$el.find('[data-region="frequency"]');

        this.$optionsPlaceholder = this.$el.find('[data-placeholder="options"]');
        this.$frequencyPlaceholder = this.$el.find('[data-placeholder="frequency"]');

        this.optionsSelector = null;
        this.frequencySelector = null;

        this.context = context;
        this.replaceOriginEl = options.replaceOriginEl;
        this.subscriptionValue = options.subscriptionValue;

        let frequency = Number(options.frequency);
        frequency = !isNaN(frequency) ? frequency : DEFAULT_OPTIONS.frequency;
        const selectedFrequency = frequency > 0 ? frequency : this.subscriptionValue;

        this.reactive({
            frequency: frequency,
            selectedOption: frequency > this.subscriptionValue ? this.subscriptionValue : frequency,
            selectedFrequency: selectedFrequency
        });

        this.cachedValues = {
            selectedOption: this.selectedOption,
            selectedFrequency: this.selectedOption > 0 ? this.selectedFrequency : 0
        };

        this.init();
    }

    [filter]() {
        return {
            selectedOption(newValue) {
                return newValue > this.subscriptionValue ? this.subscriptionValue : newValue;
            }
        }
    }

    [watch]() {
        return {
            frequency() {
                this.triggerWatchers(...arguments);
            },
            selectedOption() {
                this.updateState();
            },
            selectedFrequency() {
                this.updateState();
            }
        }
    }

    init() {
        this.setupSelectors();
        this.toggleFrequency();
    }

    enable() {
        this.optionsSelector.enable();
        this.frequencySelector.enable();
    }

    disable(onDisabledFn) {
        this.optionsSelector.disable(onDisabledFn);
        this.frequencySelector.disable(onDisabledFn);
    }

    highlight(isHighlighted) {
        if(isHighlighted) {
            this.optionsSelector.highlight(this.selectedOption !== this.cachedValues.selectedOption);
            this.frequencySelector.highlight(this.selectedFrequency !== this.cachedValues.selectedFrequency);
        } else {
            this.optionsSelector.highlight(false);
            this.frequencySelector.highlight(false);
        }
    }

    onOptionUpdated(newValue) {
        if(this.selectedOption !== newValue) {
            this.selectedOption = newValue;
        }
    }

    onFrequencyUpdate(newValue) {
        if(this.selectedFrequency !== newValue) {
            this.selectedFrequency = newValue;
        }
    }

    render() {
        if(this.$originEl) {
            if(this.replaceOriginEl) {
                this.$originEl.replaceWith(this.$el);
            } else {
                this.$originEl.html(this.$el);
            }
        }

        return this.$el;
    }

    setupSelectors() {
        const options = this.context.AutoshipOptions || [];
        const frequencyOptions = this.context.FrequencyOptions || [];

        var newArray = frequencyOptions.map(function(item){
            return Number(item.FrequencyValue);
        });

        if(!newArray.includes(this.selectedFrequency)){
            let frequencyValue = this.selectedFrequency;
            var closest = newArray.reduce(function(prev, curr) {
                return (Math.abs(curr - frequencyValue) < Math.abs(prev - frequencyValue) ? curr : prev);
              });
              this.selectedFrequency = closest;
        }

        if(options.length > 0 && frequencyOptions.length > 0) {
            this.optionsSelector = new SelectBox(this.$optionsPlaceholder, {
                ariaLabel: this.context.AutoshipSelectorAriaLabel,
                id: 'autoshipOptions',
                mods: '-txt-sm',
                name: 'AutoshipOptions',
                options: options.map(({ AutoshipName, AutoshipFrequency }) => {
                    return {
                        text: AutoshipName,
                        value: AutoshipFrequency
                    };
                }),
                replaceOriginEl: true,
                value: this.selectedOption
            });

            this.frequencySelector = new SelectBox(this.$frequencyPlaceholder, {
                ariaLabel: this.context.AutoshipSelectorAriaLabel,
                id: 'autoshipFrequency',
                mods: '-txt-sm',
                name: 'AutoshipFrequency',
                options: frequencyOptions.map(({ FrequencyName, FrequencyValue }) => {
                    return {
                        text: FrequencyName,
                        value: FrequencyValue
                    };
                }),
                replaceOriginEl: true,
                value: this.selectedFrequency
            });

            this.optionsSelector.watchValue((newValue) => this.onOptionUpdated(Number(newValue)));
            this.frequencySelector.watchValue((newValue) => this.onFrequencyUpdate(Number(newValue)));

            this.optionsSelector.render();
            this.frequencySelector.render();
        }
    }

    toggleFrequency() {
        if(this.selectedOption === 0) {
            this.$frequency.hide('fast', 'linear');
        } else {
            this.$frequency.show('fast', 'linear');
        }
    }

    triggerWatchers() {
        Object.values(this.#watch).forEach(fn => fn(...arguments));
    }

    updateState() {
        this.frequency = this.selectedOption ? this.selectedFrequency : this.selectedOption;
        this.toggleFrequency();
    }

    watchValue(fn) {
        if(isFn(fn)) {
            const id = uniqid.time();
            this.#watch[id] = fn;

            return () => {
                delete this.#watch[id];
            }
        }
    }
}
